import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/useToast'
import { Loader2, Mail, Lock } from 'lucide-react'
import { isLockedOut, recordFailedAttempt, clearAttempts } from '@/lib/rateLimiter'

export function LoginForm() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const { signIn } = useAuth()
  const navigate = useNavigate()
  const { toast } = useToast()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Check rate limiting before attempting sign in
    const lockout = isLockedOut(email)
    if (lockout.locked) {
      toast({
        title: 'Account Locked',
        description: `Too many failed attempts. Try again in ${lockout.remainingMinutes} minute${lockout.remainingMinutes !== 1 ? 's' : ''}.`,
        variant: 'destructive',
      })
      return
    }

    setIsLoading(true)

    const { error, requiresMfa } = await signIn(email, password)

    if (error) {
      recordFailedAttempt(email)
      const newLockout = isLockedOut(email)
      toast({
        title: newLockout.locked ? 'Account Locked' : 'Login Failed',
        description: newLockout.locked
          ? `Too many failed attempts. Try again in ${newLockout.remainingMinutes} minute${newLockout.remainingMinutes !== 1 ? 's' : ''}.`
          : error.message,
        variant: 'destructive',
      })
      setIsLoading(false)
      return
    }

    clearAttempts(email)

    if (requiresMfa) {
      navigate('/mfa-verify')
    } else {
      navigate('/dashboard')
    }

    setIsLoading(false)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email" className="text-sm sm:text-sm">Email</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-4 sm:w-4 text-muted-foreground" />
          <Input
            id="email"
            type="email"
            placeholder="admin@dextoro.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="pl-10"
            required
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="password" className="text-sm sm:text-sm">Password</Label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-4 sm:w-4 text-muted-foreground" />
          <Input
            id="password"
            type="password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="pl-10"
            required
          />
        </div>
      </div>

      <Button
        type="submit"
        className="w-full bg-dextoro text-black hover:bg-dextoro/90 mt-6 sm:mt-4"
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Signing in...
          </>
        ) : (
          'Sign In'
        )}
      </Button>
    </form>
  )
}
